/**
 * useRowActionRunner — the shared execution pipeline behind `rowActions`
 * (#1416) AND `detail.actions` (#1417).
 *
 * The v2 pipeline (permission re-check, custom/static confirm, result- and
 * error-branch feedback, the SHARED per-row busy claim, and query
 * invalidation) used to live inline in `RowActionsCell`. #1417 lifts it here so
 * the detail-panel footer reuses the EXACT same runner rather than copying the
 * logic — one code path, so the row cell and the footer share one busy
 * registry (a running action on a row disables both surfaces for that row).
 *
 * The hook owns the confirm-surface Modal state and returns the Modal node
 * (`confirmModal`) for the consumer to render; `run(action)` returns a settled
 * `RunOutcome`. The detail footer passes `leaveDetail`: on the success path
 * of an `afterAction: 'close'` action the runner leaves the detail and
 * refreshes through `invalidateAfterLeave`, which marks the record's detail
 * stale without reading it, so the refresh cannot refetch a record the
 * action removed (#2802). `RowActionsCell` passes no `leaveDetail` and
 * ignores the outcome; its cell actions only invalidate — byte-for-byte the
 * pre-#1417 behavior.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../primitives/Modal';
import { useResourceKit } from '@resource-kit/core/react';
import { resourceKeys } from '@resource-kit/controllers';
import { invalidateAfterLeave } from './invalidateAfterLeave';
import {
  ACTION_MODE,
  ACTION_FEEDBACK_KIND,
  AFTER_ACTION,
  type ActionFeedbackKind,
} from '@resource-kit/model';
import {
  useRowBusy,
  type ActionDefinition,
  type ActionConfirmRender,
} from '@resource-kit/controllers/react';

/**
 * The settled outcome of a `run`. `as const` map → union so a consumer can
 * never branch on a magic-string outcome the runner doesn't return.
 *   - `success`   — execute resolved; feedback shown, cache invalidated.
 *   - `error`     — execute (or payload validation) threw; error toast shown.
 *   - `cancelled` — a confirm surface was dismissed before execute.
 *   - `handoff`   — a `handoff`-mode action ran its page closure (no pipeline).
 *   - `skipped`   — a grant/eligibility re-check failed, or the row was already
 *                   busy (the double-activation guard).
 */
export const RUN_OUTCOME = {
  success: 'success',
  error: 'error',
  cancelled: 'cancelled',
  handoff: 'handoff',
  skipped: 'skipped',
} as const;
export type RunOutcome = (typeof RUN_OUTCOME)[keyof typeof RUN_OUTCOME];

// A custom-confirm surface awaiting a payload (or a cancel). The runner opens
// one per activation and resolves the promise when the render node calls
// `submit`/`cancel`.
interface PendingConfirm {
  render: (submit: (payload: unknown) => void, cancel: () => void) => ReactNode;
  resolve: (result: { ok: true; payload: unknown } | { ok: false }) => void;
}

// Narrow a `confirm` config to the custom-render variant (#1416). The static
// variant carries `title`; the custom one carries `render`.
function isCustomConfirm<T>(
  confirm: NonNullable<ActionDefinition<T>['confirm']>,
): confirm is ActionConfirmRender<T, unknown> {
  return 'render' in confirm;
}

export interface RowActionRunner<T> {
  /** Run one action through the full pipeline; resolves to its `RunOutcome`. */
  run: (action: ActionDefinition<T>) => Promise<RunOutcome>;
  /** Filter the actions the current viewer may see/run for this row (grant +
   *  per-row eligibility). An empty result ⇒ the consumer renders nothing. */
  filterVisible: (actions: ActionDefinition<T>[]) => ActionDefinition<T>[];
  /** Whether this row currently has an action in flight (shared registry). */
  isBusy: boolean;
  /** The custom-confirm Modal node — the consumer renders it once. */
  confirmModal: ReactNode;
}

export function useRowActionRunner<T>({
  row,
  rowId,
  slug,
  leaveDetail,
}: {
  row: T;
  rowId: string;
  slug: string;
  /** Leaves the detail surface. Called on success of an
   *  `afterAction: 'close'` action, which then refreshes through
   *  `invalidateAfterLeave`. Absent on the table cell. */
  leaveDetail?: () => void;
}): RowActionRunner<T> {
  const { t, access, confirm, notify } = useResourceKit();
  const queryClient = useQueryClient();
  const busy = useRowBusy();
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

  // Mirror the pending confirm into a ref so the unmount cleanup can cancel it
  // without re-subscribing on every open/close.
  const pendingConfirmRef = useRef<PendingConfirm | null>(null);
  useEffect(() => {
    pendingConfirmRef.current = pendingConfirm;
  }, [pendingConfirm]);

  // Per-instance ownership of the shared busy claim (#1417). Two consumers now
  // share ONE rowId (the table row cell + the detail footer, both under the
  // page's single RowBusyProvider), so the unmount cleanup MUST NOT blindly
  // release the row: closing an open drawer whose footer is idle would
  // otherwise wipe the claim the row cell still holds for an in-flight action,
  // re-enabling it mid-run (double-submit). This ref flips true only on the
  // branch that actually WON `busy.begin` and back to false the moment this
  // instance releases — so cleanup releases only a claim THIS instance owns.
  const heldRef = useRef(false);

  // Unmount cleanup: a 30s list refetch (or a drawer close) can drop the
  // consumer mid-flight. Cancel a pending confirm, and release the busy claim
  // ONLY if this instance currently owns it — never a sibling consumer's.
  useEffect(
    () => () => {
      pendingConfirmRef.current?.resolve({ ok: false });
      if (heldRef.current) {
        busy.end(rowId);
        heldRef.current = false;
      }
    },
    [busy, rowId],
  );

  const hasGrant = (perm: string | undefined) => perm == null || access.hasGrant(perm);

  // Closed-map dispatch of a feedback kind → the seam's notify. Total over
  // `ActionFeedbackKind`, so a new kind fails the build here rather than
  // silently falling through (no magic-string branch). The two closed maps
  // spell the same three kinds today and are still separate declarations —
  // folding them into one is #2166's call, not this seam's.
  const notifyByKind: Record<ActionFeedbackKind, (title: string, message: string) => void> = {
    [ACTION_FEEDBACK_KIND.success]: (title, message) => notify('success', title, message),
    [ACTION_FEEDBACK_KIND.info]: (title, message) => notify('info', title, message),
    [ACTION_FEEDBACK_KIND.error]: (title, message) => notify('error', title, message),
  };

  const filterVisible = (actions: ActionDefinition<T>[]) =>
    actions.filter(
      (action) =>
        hasGrant(action.permission) && (action.isEligible == null || action.isEligible(row)),
    );

  const keys = resourceKeys(slug);

  // Open the custom confirm surface and await the collected (validated-later)
  // payload. Resolves `{ ok: false }` on cancel.
  const openCustomConfirm = (
    confirmConfig: ActionConfirmRender<T, unknown>,
  ): Promise<{ ok: true; payload: unknown } | { ok: false }> =>
    new Promise((resolve) => {
      setPendingConfirm({
        render: (submit, cancel) => confirmConfig.render(row, submit, cancel),
        resolve,
      });
    });

  const closeCustomConfirm = () => setPendingConfirm(null);

  const run = async (action: ActionDefinition<T>): Promise<RunOutcome> => {
    // Re-check the grant + eligibility at execution time: a grant revoked
    // between paint and click must not slip a mutating call through.
    if (!hasGrant(action.permission)) return RUN_OUTCOME.skipped;
    if (action.isEligible != null && !action.isEligible(row)) return RUN_OUTCOME.skipped;

    // Hand-off (#1416 capability 4): no confirm, no toast, no busy — just hand
    // control back to the page's captured closure.
    if (action.mode === ACTION_MODE.handoff) {
      action.onAction(row, undefined as never);
      return RUN_OUTCOME.handoff;
    }

    // Atomic claim on the shared registry — the synchronous double-activation
    // guard, observable to every consumer (row cell + detail footer). Mark this
    // instance as the OWNER so a mid-flight unmount releases only its own claim.
    if (!busy.begin(rowId)) return RUN_OUTCOME.skipped;
    heldRef.current = true;

    const actionLabel = t(action.label, { defaultValue: action.label });
    // Calling with an optional payload: the no-confirm / static-confirm path
    // invokes `onAction(row)` with EXACTLY one arg (byte-equivalent to the
    // pre-v2 pipeline); only a custom-confirm surface passes a second, parsed
    // payload arg.
    const invoke = action.onAction as (row: T, payload?: unknown) => unknown;
    let outcome: RunOutcome = RUN_OUTCOME.success;
    try {
      let result: unknown;
      if (action.confirm && isCustomConfirm(action.confirm)) {
        const collected = await openCustomConfirm(action.confirm);
        closeCustomConfirm();
        if (!collected.ok) return RUN_OUTCOME.cancelled;
        // zod-validate the collected payload BEFORE execute — an invalid
        // payload throws here and routes to the error branch (execute never
        // runs on unvalidated input).
        const payload = action.confirm.schema.parse(collected.payload);
        result = await invoke(row, payload);
      } else {
        if (action.confirm && 'title' in action.confirm) {
          const yes = await confirm({
            title: t(action.confirm.title, { defaultValue: action.confirm.title }),
            message: action.confirm.description
              ? t(action.confirm.description, { defaultValue: action.confirm.description })
              : t('rk.resource.confirmProceed'),
            intent: action.intent === 'danger' ? 'danger' : 'default',
          });
          if (!yes) return RUN_OUTCOME.cancelled;
        }
        result = await invoke(row);
      }

      // Result-branch feedback (#1416 capability 2): the closed-map kind routes
      // to the matching toast. Absent ⇒ the historical single success toast.
      if (action.feedback) {
        const fb = action.feedback(result);
        notifyByKind[fb.kind](actionLabel, t(fb.message, { defaultValue: fb.message }));
      } else {
        const message = action.formatResult
          ? action.formatResult(result)
          : t('rk.resource.actionCompleted');
        notify('success', actionLabel, message);
      }
      if (action.afterAction === AFTER_ACTION.close && leaveDetail) {
        // The action leaves the detail: refresh the namespace but do not read
        // this record's detail again, as the action may have removed it
        // (#2802). Its stale mark makes a later reopen read it.
        leaveDetail();
        void invalidateAfterLeave(queryClient, keys, rowId);
      } else {
        // Invalidate both list AND any open detail cache: an action can change
        // a record shown in the detail panel, so a list-only refetch would
        // leave the panel stale.
        queryClient.invalidateQueries({ queryKey: keys.all() });
      }
      outcome = RUN_OUTCOME.success;
    } catch (e: unknown) {
      // errorFeedback overrides the error body (e.g. 409 → alreadyResolved);
      // otherwise fall back to the raw error message. Call errorFeedback ONCE.
      let msg: string;
      if (action.errorFeedback) {
        const key = action.errorFeedback(e);
        msg = t(key, { defaultValue: key });
      } else {
        msg = e instanceof Error ? e.message : t('rk.error.unknown');
      }
      notify('error', t('rk.resource.actionFailed', { name: actionLabel }), msg);
      outcome = RUN_OUTCOME.error;
    } finally {
      closeCustomConfirm();
      // Symmetric with the unmount cleanup: release the shared claim ONLY if
      // this instance still owns it. A mid-flight unmount may already have
      // released it (and cleared heldRef) — after which a successor instance
      // can re-claim the same rowId. An unconditional release here would then
      // wipe the successor's claim, re-enabling the row mid-run (double-submit).
      // Whoever runs first (cleanup or this finally) performs the sole release.
      if (heldRef.current) {
        busy.end(rowId);
        heldRef.current = false;
      }
    }
    return outcome;
  };

  const confirmModal = (
    // Custom-confirm surface (#1416 capability 1). Hosted in the house Modal so
    // Escape / backdrop close come for free and map to cancel (resolve
    // `{ ok: false }`); the definition's `render` owns its content.
    <Modal
      isOpen={pendingConfirm != null}
      onClose={() => pendingConfirm?.resolve({ ok: false })}
      size="md"
    >
      {pendingConfirm && (
        <div className="p-6">
          {pendingConfirm.render(
            (payload) => pendingConfirm.resolve({ ok: true, payload }),
            () => pendingConfirm.resolve({ ok: false }),
          )}
        </div>
      )}
    </Modal>
  );

  return { run, filterVisible, isBusy: busy.isBusy(rowId), confirmModal };
}
